import Dialog from 'components/Dialog';
import React, {ChangeEvent} from 'react';
import {foldersBuilder, INav} from 'features/layout/redux/helpers/FoldersHelper';
import {useTranslation} from 'react-i18next';
import {usePostApi} from 'common/hooks/usePostApi';
import api from 'api';
import {DiplomatObject} from 'types/DiplomatObject';
import FolderRecursiveTreeView from 'components/FolderRecursiveTreeView';
import Panel from 'components/Panel';
import {PrimaryButton} from 'components/buttons/PrimaryButton';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import {AlertDialog} from 'components/dialogs/AlertDialog';

type MoveDialogProps = {
   data: INav;
   header: string;
   onConfirm: (parentFolderId: string) => void;
   onClose: () => void;
};

const MoveDialog = ({data, header, onConfirm, onClose}: MoveDialogProps) => {
   const {t} = useTranslation();
   const [tree, setTree] = React.useState<INav[]>([]);
   const [parentFolderId, setParentFolderId] = React.useState<string>('');
   const handleFolderSelect = (_: ChangeEvent<{}>, nodeIds: string) => setParentFolderId(nodeIds);
   const options = React.useMemo(
      () => ({
         onSuccess: (data: {folders: INav[]}) => {
            if (data.folders.length === 1) {
               return;
            }
            const tree = foldersBuilder.buildFoldersWithObjects(data.folders);
            setTree(tree);
         },
         onFailure: onClose,
      }),
      [onClose]
   );
   const {request: fetchTargets, loading} = usePostApi<{folders: INav[]}>(
      api.folderTargets,
      options
   );
   React.useEffect(() => {
      fetchTargets(JSON.stringify(DiplomatObject.cast(data, {stripUnknown: true})));
   }, [data, fetchTargets]);

   const handleMove = () => {
      onConfirm(parentFolderId);
   };

   if (loading) {
      return null;
   }

   if (tree.length === 0) {
      return (
         <AlertDialog
            title={t('Move Error')}
            content={t('There are no destination folders for this move.')}
            onClose={onClose}
         />
      );
   }

   return (
      <Dialog maxWidth="sm" header={header} close={onClose}>
         <Panel>
            <Box minHeight={200}>
               <FolderRecursiveTreeView nodes={tree} onNodeSelect={handleFolderSelect} />
            </Box>
         </Panel>
         <Grid container spacing={2} alignItems={'center'} justifyContent={'center'}>
            <Grid item>
               <PrimaryButton disabled={!parentFolderId} onClick={handleMove}>
                  {t('OK')}
               </PrimaryButton>
            </Grid>
            <Grid item>
               <Button onClick={onClose}>{t('Cancel')}</Button>
            </Grid>
         </Grid>
      </Dialog>
   );
};

export default MoveDialog;
